import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Table, TableWrapper } from "@/components/ui/table";
import { fetchAdminRoles, type AdminRoleDefinition } from "@/lib/api";
import type { PermissionLevel, RoleDefinition } from "@/types";

const RESOURCE_COLUMNS: string[] = [
  "Users",
  "Roles",
  "Groups",
  "Service Accounts",
  "Audit Log",
  "Wallets",
  "Transfers",
  "Policies",
  "Deployment",
];

const PERM_CLASS: Record<PermissionLevel, string> = {
  Full: "font-bold text-success",
  Read: "font-semibold text-info",
  Create: "font-semibold text-warning",
  Approve: "font-semibold text-warning",
  Own: "font-semibold text-primary",
  Use: "font-semibold text-info",
  Staging: "font-semibold text-warning",
  Scoped: "font-semibold text-muted",
  Keys: "font-semibold text-info",
  Dev: "font-semibold text-info",
  None: "text-subtle",
};

const SCOPE_TO_RESOURCE: Record<string, string> = {
  WALLETS_READ: "Wallets",
  WALLETS_WRITE: "Wallets",
  BALANCES_READ: "Wallets",
  TRANSFERS_READ: "Transfers",
  TRANSFERS_WRITE: "Transfers",
  TRANSFERS_DRAFT: "Transfers",
  TRANSFERS_ESTIMATE: "Transfers",
  APPROVALS_READ: "Policies",
  APPROVALS_APPROVE: "Policies",
  APPROVALS_REJECT: "Policies",
  POLICIES_READ: "Policies",
  POLICIES_WRITE: "Policies",
  RECONCILIATION_READ: "Audit Log",
  RECONCILIATION_MANAGE: "Audit Log",
  RECONCILIATION_EXPORT: "Audit Log",
  AUDIT_READ: "Audit Log",
  AUDIT_EXPORT: "Audit Log",
  CONFIG_READ: "Deployment",
  CONFIG_WRITE: "Deployment",
  ADMIN_USERS_READ: "Users",
  ADMIN_USERS_WRITE: "Users",
  ADMIN_USERS_DEACTIVATE: "Users",
  AUTH_ROLES_MANAGE: "Roles",
  AUTH_SESSIONS_VERIFY: "Groups",
  AUTH_SESSIONS_MANAGE: "Groups",
  NOTIFICATIONS_READ: "Service Accounts",
  NOTIFICATIONS_WRITE: "Service Accounts",
};

const WRITE_SCOPES = new Set([
  "WALLETS_WRITE", "TRANSFERS_WRITE", "TRANSFERS_DRAFT", "APPROVALS_APPROVE",
  "APPROVALS_REJECT", "POLICIES_WRITE", "RECONCILIATION_MANAGE",
  "RECONCILIATION_EXPORT", "AUDIT_EXPORT", "CONFIG_WRITE", "ADMIN_USERS_WRITE",
  "ADMIN_USERS_DEACTIVATE", "AUTH_ROLES_MANAGE", "AUTH_SESSIONS_MANAGE",
  "NOTIFICATIONS_WRITE",
]);

function derivePermissionLevel(scopes: string[], resource: string): PermissionLevel {
  const resourceScopes = scopes.filter(s => SCOPE_TO_RESOURCE[s] === resource);
  if (resourceScopes.length === 0) return "None";
  const hasWrite = resourceScopes.some(s => WRITE_SCOPES.has(s));
  if (hasWrite) return "Full";
  return "Read";
}

function apiRoleToRoleDefinition(role: AdminRoleDefinition): RoleDefinition {
  const permissions: Record<string, PermissionLevel> = {};
  for (const col of RESOURCE_COLUMNS) {
    permissions[col] = derivePermissionLevel(role.scopes, col);
  }
  return {
    id: role.roleName,
    label: role.displayName || role.roleName.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()),
    description: `Scopes: ${role.scopes.length} · Permissions: ${role.permissions.length}`,
    permissions,
  };
}

export function RoleCatalog() {
  const [roles, setRoles] = useState<RoleDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchAdminRoles()
      .then((result) => {
        if (cancelled) return;
        if (result.error) {
          setError(`Failed to load roles: ${result.error}`);
        } else {
          setRoles(result.roles.map(apiRoleToRoleDefinition));
        }
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setError("Unable to connect to the admin API.");
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const activeRoles = useMemo(() => roles, [roles]);

  return (
    <div>
      <nav className="mb-5 flex items-center gap-1.5 text-[13px]" aria-label="Breadcrumb">
        <Link to="/admin/users" className="text-primary no-underline hover:underline">Identity</Link>
        <span className="text-muted" aria-hidden>›</span>
        <span className="font-medium text-text">Role Catalog</span>
      </nav>

      <div className="mb-5">
        <h1 className="text-xl font-extrabold text-ink">Role Catalog</h1>
        <p className="mt-1 text-sm text-muted">
          Permission matrix across every system role. Read-only — role permissions are fixed by the platform and cannot be edited here.
        </p>
      </div>

      {error && !loading && (
        <div className="mb-4 rounded-lg border border-danger bg-danger-soft px-4 py-3 text-sm text-danger">
          {error}
        </div>
      )}

      {loading && (
        <div className="mb-4 text-center text-sm text-muted">Loading roles...</div>
      )}

      <TableWrapper>
        <Table className="dense">
          <thead>
            <tr className="border-b border-line bg-bg">
              <th className="min-w-[220px] px-4 py-[9px] text-left text-[10.5px] font-semibold normal-case tracking-normal text-muted">Role</th>
              {RESOURCE_COLUMNS.map((col) => (
                <th key={col} className="whitespace-nowrap px-4 py-[9px] text-left text-[10.5px] font-semibold normal-case tracking-normal text-muted">{col}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {activeRoles.map((role) => (
              <tr key={role.id} className="border-b border-line last:border-b-0">
                <td className="px-4 py-[11px] align-top">
                  <div className="font-mono text-[13px] font-semibold text-ink">{role.label}</div>
                  <div className="mt-0.5 max-w-[260px] text-[11.5px] leading-snug text-muted">{role.description}</div>
                </td>
                {RESOURCE_COLUMNS.map((col) => {
                  const level = role.permissions[col] ?? "None";
                  return (
                    <td key={col} className={`whitespace-nowrap px-4 py-[11px] text-[12.5px] ${PERM_CLASS[level]}`}>
                      {level}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </Table>
        {activeRoles.length === 0 && !loading && !error && (
          <div className="px-4 py-10 text-center text-sm text-muted">No roles found.</div>
        )}
      </TableWrapper>
    </div>
  );
}
